import { immutableSort, uniquePrimitives } from './array.utils';
import { normalizeEmail } from './string.utils';

export type AccessRightPermissionComparisonInput = {
  permissions: {
    canManageAccessRights?: boolean;
    canManageStudents?: boolean;
    canManageCompetences?: boolean;
    canManagePerformanceAssessments?: boolean;
    subjects?: Array<{ id: number }> | null;
  };
  schoolSubjects: Array<{ id: number }>;
};

export type AccessRightComparisonInput = AccessRightPermissionComparisonInput & {
  user: {
    id?: number | null;
    email?: string | null;
  };
  userRoleType?: string | null;
  isInvitation?: boolean;
};

export type AccessRightComparisonOptions = {
  compareUserRoleType?: boolean;
};

export const getAccessRightUserKey = (accessRight: AccessRightComparisonInput): string => {
  const normalizedEmail = normalizeEmail(accessRight.user.email);
  if (normalizedEmail) return normalizedEmail;

  return `${accessRight.isInvitation ? 'invitation' : 'user'}:${accessRight.user.id ?? ''}`;
};

export const getSortedAccessRightSubjectIds = (accessRight: AccessRightPermissionComparisonInput): number[] => {
  const subjects =
    accessRight.permissions.subjects !== undefined
      ? (accessRight.permissions.subjects ?? [])
      : accessRight.schoolSubjects;
  return immutableSort(
    subjects.map((schoolSubject) => schoolSubject.id),
    (a, b) => a - b,
  );
};

const sortSubjectIds = (subjectIds: number[]): number[] => immutableSort(subjectIds, (a, b) => a - b);

/** Whether the access right covers at least one of the subjects; an unrestricted one covers them all. */
export const hasAccessToAnySubject = (
  accessRight: AccessRightPermissionComparisonInput,
  subjectIds: number[],
): boolean => {
  const restrictedSubjectIds = getSortedAccessRightSubjectIds(accessRight);
  return !restrictedSubjectIds.length || subjectIds.some((subjectId) => restrictedSubjectIds.includes(subjectId));
};

/**
 * The subject restriction after granting or revoking access to subjects. Granting keeps an unrestricted access right
 * unrestricted; revoking restricts it to the remaining class subjects. Returns null when no subject would remain,
 * because an empty restriction means every subject. `classSubjectIds` are the class subjects the editor knows.
 */
export const getSubjectIdsAfterAccessChange = (
  accessRight: AccessRightPermissionComparisonInput,
  change: { subjectIds: number[]; hasAccess: boolean; classSubjectIds: number[] },
): number[] | null => {
  const restrictedSubjectIds = getSortedAccessRightSubjectIds(accessRight);
  if (change.hasAccess) {
    if (!restrictedSubjectIds.length) return [];
    return sortSubjectIds(uniquePrimitives([...restrictedSubjectIds, ...change.subjectIds]));
  }

  const currentSubjectIds = restrictedSubjectIds.length ? restrictedSubjectIds : change.classSubjectIds;
  const remainingSubjectIds = currentSubjectIds.filter((subjectId) => !change.subjectIds.includes(subjectId));
  return remainingSubjectIds.length ? sortSubjectIds(remainingSubjectIds) : null;
};

export const hasEqualPermissionsAndSubjectAccess = (
  a: AccessRightPermissionComparisonInput,
  b: AccessRightPermissionComparisonInput,
): boolean => {
  const aSubjectIds = getSortedAccessRightSubjectIds(a);
  const bSubjectIds = getSortedAccessRightSubjectIds(b);
  return (
    a.permissions.canManageAccessRights === b.permissions.canManageAccessRights &&
    a.permissions.canManageStudents === b.permissions.canManageStudents &&
    a.permissions.canManagePerformanceAssessments === b.permissions.canManagePerformanceAssessments &&
    a.permissions.canManageCompetences === b.permissions.canManageCompetences &&
    aSubjectIds.length === bSubjectIds.length &&
    aSubjectIds.every((id, index) => id === bSubjectIds[index])
  );
};

export const hasEqualAccessRight = (
  a: AccessRightComparisonInput,
  b: AccessRightComparisonInput,
  options: AccessRightComparisonOptions = {},
): boolean => {
  return (
    hasEqualPermissionsAndSubjectAccess(a, b) && (!options.compareUserRoleType || a.userRoleType === b.userRoleType)
  );
};

export const haveEqualAccessRights = (
  a: AccessRightComparisonInput[],
  b: AccessRightComparisonInput[],
  options: AccessRightComparisonOptions = {},
): boolean => {
  if (a.length !== b.length) return false;

  const aByKey = new Map(a.map((accessRight) => [getAccessRightUserKey(accessRight), accessRight]));
  const bByKey = new Map(b.map((accessRight) => [getAccessRightUserKey(accessRight), accessRight]));
  if (aByKey.size !== a.length || bByKey.size !== b.length || aByKey.size !== bByKey.size) return false;

  for (const [key, accessRight] of aByKey) {
    const matchingAccessRight = bByKey.get(key);
    if (!matchingAccessRight || !hasEqualAccessRight(accessRight, matchingAccessRight, options)) {
      return false;
    }
  }

  return true;
};
